"use client";

import Link from "next/link";
import * as React from "react";
import { LocaleSwitch } from "./ui/locale-switch";

import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu";
import { siteConfig } from "@/lib/config";
import { useTranslations } from "@/lib/hooks/use-translations";
import { cn } from "@/lib/utils";

export default function NavigationMenuDemo() {
  const { locale } = useTranslations();
  return (
    <NavigationMenu>
      <NavigationMenuList className="items-center">
        {siteConfig.header(locale).map((item, index) => (
          <NavigationMenuItem key={index}>
            {item.trigger ? (
              <>
                <NavigationMenuTrigger onClick={(e) => e.preventDefault()}>
                  {item.trigger}
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <div className="grid gap-3 p-6 w-[400px] md:w-[500px] lg:w-[600px]">
                    <ul className="flex flex-col gap-3">
                      {item.content.items.map((subItem, subIndex) => (
                        <ListItem
                          key={subIndex}
                          href={siteConfig.getLocalizedUrl(subItem.href, locale)}
                          title={subItem.title}
                          className="hover:bg-primary/10"
                        >
                          {subItem.description}
                        </ListItem>
                      ))}
                    </ul>
                  </div>
                </NavigationMenuContent>
              </>
            ) : (
              <Link
                  href={siteConfig.getLocalizedUrl(item.href || "", locale)}
                  className={navigationMenuTriggerStyle()}
                  passHref 
                >
                  {item.label}
                </Link>
            )}
          </NavigationMenuItem>
        ))}
        <NavigationMenuItem>
          <LocaleSwitch />
        </NavigationMenuItem>
      </NavigationMenuList>
    </NavigationMenu>
  );
}

interface ListItemProps extends React.ComponentPropsWithoutRef<"a"> {
  title: string;
  href: string;
}

const ListItem = React.forwardRef<
  React.ElementRef<"a">,
  ListItemProps
>(({ className, title, children, href, ...props }, ref) => {
  return (
    <li>
      <NavigationMenuLink asChild>
        <Link href={href} passHref legacyBehavior>
          <a
            ref={ref}
            className={cn(
              "block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-primary/10 focus:text-accent-foreground",
              className
            )}
            {...props}
          >
            <div className="text-sm font-medium leading-none">{title}</div>
            <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
              {children}
            </p>
          </a>
        </Link>
      </NavigationMenuLink>
    </li>
  );
});

ListItem.displayName = "ListItem";
